/**
 * Root application component
 * Configures providers and routing
 */
import React, { useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { CssBaseline } from '@mui/material';
import { queryClient } from '@core/config/react-query.config';
import { ThemeContextProvider } from '@core/contexts';
import { NotificationProvider } from '@components/common/NotificationProvider/NotificationProvider';
import { startTokenRefreshScheduler } from '@services/api/tokenRefreshScheduler';
import AppRoutes from '@routes/AppRoutes';
import { APP_NAME } from '@core/constants/app.constants';

/**
 * Root application component
 * @returns JSX Element
 */
const App: React.FC = () => {
  /**
   * Proactive token refresh (interceptors register synchronously from main.tsx via axios.instance).
   */
  useEffect(() => {
    startTokenRefreshScheduler();
  }, []);

  /**
   * Set document title on app mount
   */
  useEffect(() => {
    document.title = APP_NAME;
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeContextProvider>
        <CssBaseline />
        <NotificationProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </NotificationProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </ThemeContextProvider>
    </QueryClientProvider>
  );
};

export default App;
